<template>
  <LoadingSpinner v-if="isLoading" />
  <div v-else class="show-subcontractor">
    <PageHeader 
      :title="sub.name" 
      :action="{ title: 'Edit', to: { name: 'EditSubcontractor', params: { id: sub.id } } }" 
    />

    <div class="subcontractor-details">
      <div class="subcontractor-header">
        <div class="subcontractor-avatar">
          <BaseAvatar class="size-40" />
        </div>
        <div class="subcontractor-info">
          <h2 class="text-2xl font-semibold text-slate-950">{{ sub.name }}</h2>
          <div class="contact-details">
            <div class="detail-item">
              <span class="text-slate-500">Email:</span>
              <span>{{ sub.email }}</span>
            </div>
            <div class="detail-item">
              <span class="text-slate-500">Phone:</span>
              <span>{{ sub.phone }}</span>
            </div>
            <div class="detail-item">
              <span class="text-slate-500">Address:</span>
              <span>{{ sub.address }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="subcontractor-sections">
        <div class="section">
          <h3 class="text-xl font-semibold mb-4">Work Orders ({{ workOrders.length }})</h3>
          <BaseTable
            :items="workOrders"
            :fields="fields"
            :is-searchable="true"
            :clickable="true"
          >
            <template #status-data="{ row }">
              <span :class="['badge', row.status === 'open' ? 'badge-success' : 'badge-danger']">
                {{ row.status }}
              </span>
            </template>
            <template #date-data="{ row }">
              {{ new Date(row.date).toLocaleDateString() }}
            </template>
            <template #workOrderId-data="{ row }">
              <router-link :to="{ name: 'ShowWorkOrder', params: { id: row.id } }">
                {{ row.workOrderId }}
              </router-link>
            </template>
          </BaseTable>
        </div>

        <div class="section">
          <h3 class="text-xl font-semibold mb-4">Bills ({{ bills.length }})</h3>
          <BaseTable
            :items="bills"
            :fields="fieldsBill"
            :is-searchable="true"
            :clickable="true"
          >
            <template #status-data="{ row }">
              <span
                :class="[
                  'badge',
                  row.status === 'outstanding' ? 'badge-warning' :
                  row.status === 'paid' ? 'badge-success' :
                  'badge-danger'
                ]">
                {{ row.status }}
              </span>
            </template>
            <template #dueDate-data="{ row }">
              {{ new Date(row.dueDate).toLocaleDateString() }}
            </template>
            <template #billNumber-data="{ row }">
              <router-link :to="{ name: 'ShowBill', params: { id: row.id } }">
                {{ row.billNumber }}
              </router-link>
            </template>
          </BaseTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import { useHead } from '@unhead/vue'
import { useQuery, QueryClient, queryOptions } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'
import SubcontractorService from '@/services/SubcontractorService'

useHead({ title: 'Show Subcontrator' })

const subId = useRoute().params.id as string || ''
const workOrders = ref([])
const bills = ref([])
const fields = ref([
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
  { key: 'workOrderId', label: 'WO#' }
])
const fieldsBill = ref([
  { key: 'status', label: 'Status' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'billNumber', label: 'BILL#' }
])

const queryClient = new QueryClient()
const subcontractorService = new SubcontractorService()
const { data: sub, isLoading } = useQuery({
  queryKey: ['subcontractor',subId],
  queryFn: () => subcontractorService.find(subId)
})

watch(sub, async (sub) => {
  const billsQuery = (id: string) => queryOptions({
    queryKey: ['bills', id],
    queryFn: () => subcontractorService.getBills(id),
    enabled: !!sub.id
  })

  const workOrdersQuery = (id: string) => queryOptions({
    queryKey: ['workOrders', id],
    queryFn: () => subcontractorService.getWorkOrders(id),
    enabled: !!sub.id
  })

  bills.value = await queryClient.fetchQuery(billsQuery(sub.id))
  workOrders.value = await queryClient.fetchQuery(workOrdersQuery(sub.id))
})
</script>

<style lang="scss" scoped>
.show-subcontractor {
  .subcontractor-details {
    @apply space-y-8;
  }

  .subcontractor-header {
    @apply flex items-start gap-8 p-6 bg-white rounded-lg shadow-sm;
  }

  .subcontractor-avatar {
    @apply flex-shrink-0;
  }

  .subcontractor-info {
    @apply flex-1;
  }

  .contact-details {
    @apply mt-4 space-y-2;
  }

  .detail-item {
    @apply flex gap-2;
  }

  .subcontractor-sections {
    @apply space-y-8;
  }

  .section {
    @apply bg-white p-6 rounded-lg shadow-sm;
  }

  .badge {
    @apply px-2 py-1 rounded text-sm font-medium;

    &-success {
      @apply bg-green-100 text-green-800;
    }

    &-danger {
      @apply bg-red-100 text-red-800;
    }

    &-warning {
      @apply bg-yellow-100 text-yellow-800;
    }
  }
}
</style>
